import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogOffering, CatalogRunningNumberKeys, CatalogService, CompleteServiceOrderPayload, ConsumeServicePartsPayload,
  InventoryLot, InventorySerial, ServiceCompletionEvidence, ServiceOrder, ServiceResource
} from './catalog.service';

type ServiceTab = 'general' | 'execution';

@Component({
  standalone: true,
  selector: 'ain-service-order-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TextareaModule, TableModule, CheckboxModule, SlicePipe
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.service.orders.createTitle') : t('catalog.service.orders.detailTitle')" [description]="t('catalog.service.orders.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !order()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.service.orders.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.service.orders.newRecord') : order()!.orderNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.service.orders.createHint') : order()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/services/orders']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
            @if (!isCreateMode() && canSchedule()) {
              <button pButton type="button" [label]="t('catalog.service.action.schedule')" severity="secondary" [disabled]="acting()" (click)="schedule()"></button>
            }
            @if (!isCreateMode() && canAssign()) {
              <button pButton type="button" [label]="t('catalog.service.action.assign')" severity="info" [disabled]="acting()" (click)="assign()"></button>
            }
            @if (!isCreateMode() && canStart()) {
              <button pButton type="button" [label]="t('catalog.service.action.start')" severity="success" [disabled]="acting()" (click)="start()"></button>
            }
            @if (!isCreateMode() && canComplete()) {
              <button pButton type="button" [label]="t('catalog.service.action.complete')" severity="warn" [disabled]="acting()" (click)="complete()"></button>
            }
            @if (!isCreateMode() && canCustomerApprove()) {
              <button pButton type="button" [label]="t('catalog.service.action.customerApprove')" severity="success" [disabled]="acting()" (click)="customerApprove()"></button>
            }
          </div>
        </section>

        <nav class="tab-nav">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          @if (!isCreateMode()) {
            <button type="button" [class.active]="activeTab() === 'execution'" (click)="activeTab.set('execution')">{{ t('catalog.service.executionTab') }}</button>
          }
        </nav>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.service.orderNumber') }}</span>
              <input pInputText type="text" [(ngModel)]="form.orderNumber" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.service.title') }}</span>
              <input pInputText type="text" [(ngModel)]="form.title" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.service.priority') }}</span>
              <p-select [options]="priorityOptions" [(ngModel)]="form.priority" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.service.slaPolicyCode') }}</span>
              <input pInputText type="text" [(ngModel)]="form.slaPolicyCode" [disabled]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('catalog.service.customerReference') }}</span>
              <input
                pInputText
                type="text"
                [ngModel]="form.customerReferenceNumber"
                (ngModelChange)="onCustomerReferenceChange($event)"
                [disabled]="!isCreateMode() || isAutoCustomerReference"
                [placeholder]="t('catalog.tracking.numberAuto')" />
              @if (isCreateMode() && isAutoCustomerReference) {
                <button type="button" class="manual-link" (click)="enableManualCustomerReference()">{{ t('catalog.tracking.enterManually') }}</button>
              }
            </label>
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [options]="offeringOptions()" [(ngModel)]="form.offeringId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.tracking.serialId') }}</span>
              <p-select [options]="serialOptions()" [(ngModel)]="form.serialId" optionLabel="label" optionValue="value" [disabled]="!isCreateMode()" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label class="full">
              <span>{{ t('catalog.service.description') }}</span>
              <textarea pTextarea rows="3" [(ngModel)]="form.description" [disabled]="!isCreateMode()"></textarea>
            </label>
            @if (!isCreateMode()) {
              <label>
                <span>{{ t('catalog.service.scheduledStart') }}</span>
                <input pInputText type="datetime-local" [(ngModel)]="scheduleForm.startLocal" />
              </label>
              <label>
                <span>{{ t('catalog.service.scheduledEnd') }}</span>
                <input pInputText type="datetime-local" [(ngModel)]="scheduleForm.endLocal" />
              </label>
            }
          </section>
        } @else {
          <section class="form-grid">
            <label>
              <span>{{ t('catalog.service.resource') }}</span>
              <p-select [options]="resourceOptions()" [(ngModel)]="assignForm.resourceId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" />
            </label>
            <label class="checkbox-row">
              <p-checkbox [(ngModel)]="assignForm.isPrimary" [binary]="true" />
              <span>{{ t('catalog.service.primaryAssignment') }}</span>
            </label>
          </section>

          <section class="lines-panel">
            <h4>{{ t('catalog.service.assignments.title') }}</h4>
            <p-table [value]="order()?.assignments ?? []">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.service.resource') }}</th>
                  <th>{{ t('catalog.service.primaryAssignment') }}</th>
                  <th>{{ t('catalog.service.assignedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ resourceLabel(row.resourceId) }}</td>
                  <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
                  <td>{{ row.assignedAtUtc }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>

          @if (canConsumeParts()) {
            <section class="form-grid consume-panel">
              <h4 class="full">{{ t('catalog.service.consumeParts.title') }}</h4>
              <label>
                <span>{{ t('catalog.inventory.offeringId') }}</span>
                <p-select [options]="inventoryOfferingOptions()" [(ngModel)]="consumeForm.offeringId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.tracking.serialId') }}</span>
                <p-select [options]="consumeSerialOptions()" [(ngModel)]="consumeForm.serialId" optionLabel="label" optionValue="value" appendTo="body" />
              </label>
              <label>
                <span>{{ t('catalog.inventory.quantity') }}</span>
                <p-inputNumber [(ngModel)]="consumeForm.quantity" [min]="0.001" />
              </label>
              <label>
                <span>{{ t('catalog.service.consumeParts.reference') }}</span>
                <input pInputText type="text" [(ngModel)]="consumeForm.referenceNumber" />
              </label>
              <label>
                <span>{{ t('catalog.service.unitCost') }}</span>
                <p-inputNumber [(ngModel)]="consumeForm.unitCost" [min]="0" />
              </label>
              <label class="full">
                <button pButton type="button" [label]="t('catalog.service.action.consumeParts')" [disabled]="acting()" (click)="consumeParts()"></button>
              </label>
            </section>
          }

          <section class="lines-panel">
            <h4>{{ t('catalog.service.consumptions.title') }}</h4>
            <p-table [value]="order()?.consumptions ?? []">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.tracking.serialId') }}</th>
                  <th>{{ t('catalog.inventory.quantity') }}</th>
                  <th>{{ t('catalog.service.consumeParts.reference') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ offeringLabel(row.offeringId) }}</td>
                  <td>{{ row.serialId || '-' }}</td>
                  <td>{{ row.quantity }}</td>
                  <td>{{ row.referenceNumber }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>

          @if (canComplete()) {
            <section class="form-grid">
              <label class="full">
                <span>{{ t('catalog.service.completionNotes') }}</span>
                <textarea pTextarea rows="3" [(ngModel)]="completeForm.completionNotes"></textarea>
              </label>
              <label class="full">
                <span>{{ t('catalog.service.checklistResults') }}</span>
                <textarea pTextarea rows="4" [(ngModel)]="completeForm.checklistResultsJson"></textarea>
              </label>
              <label class="checkbox-row full">
                <p-checkbox [(ngModel)]="completeForm.requiresCustomerApproval" [binary]="true" />
                <span>{{ t('catalog.service.requiresCustomerApproval') }}</span>
              </label>
              <label>
                <span>{{ t('catalog.service.serviceRevenueAmount') }}</span>
                <p-inputNumber [(ngModel)]="completeForm.serviceRevenueAmount" [min]="0" />
              </label>
            </section>
          }

          <section class="lines-panel">
            <h4>{{ t('catalog.service.evidence.title') }}</h4>
            @if (canConsumeParts() || canComplete()) {
              <div class="form-grid evidence-form">
                <label>
                  <span>{{ t('catalog.service.evidence.type') }}</span>
                  <p-select [options]="evidenceTypeOptions()" [(ngModel)]="evidenceForm.evidenceType" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="onEvidenceTypeChange()" />
                </label>
                <label class="full">
                  <span>{{ t('catalog.service.evidence.caption') }}</span>
                  <input pInputText type="text" [(ngModel)]="evidenceForm.caption" />
                </label>

                @if (evidenceForm.evidenceType === 'Photo') {
                  <label class="full">
                    <span>{{ t('catalog.service.evidence.uploadPhoto') }}</span>
                    <input type="file" accept="image/*" (change)="onPhotoSelected($event)" />
                  </label>
                  @if (evidenceForm.contentReference.startsWith('data:image')) {
                    <div class="full preview">
                      <img [src]="evidenceForm.contentReference" alt="preview" />
                    </div>
                  }
                } @else if (evidenceForm.evidenceType === 'Signature') {
                  <div class="full signature-panel">
                    <span>{{ t('catalog.service.evidence.signaturePad') }}</span>
                    <canvas #signatureCanvas width="420" height="140" (mousedown)="startSignature($event)" (mousemove)="drawSignature($event)" (mouseup)="endSignature()" (mouseleave)="endSignature()" (touchstart)="startSignatureTouch($event)" (touchmove)="drawSignatureTouch($event)" (touchend)="endSignature()"></canvas>
                    <div class="signature-actions">
                      <button pButton type="button" severity="secondary" [label]="t('catalog.service.evidence.clearSignature')" (click)="clearSignature()"></button>
                      <button pButton type="button" [label]="t('catalog.service.evidence.applySignature')" (click)="applySignature()"></button>
                    </div>
                  </div>
                } @else {
                  <label class="full">
                    <span>{{ t('catalog.service.evidence.note') }}</span>
                    <textarea pTextarea rows="3" [(ngModel)]="evidenceForm.contentReference" [placeholder]="t('catalog.service.evidence.noteHint')"></textarea>
                  </label>
                }

                <button pButton type="button" class="full" [label]="t('catalog.service.evidence.add')" [disabled]="acting()" (click)="addEvidence()"></button>
              </div>
            }
            <p-table [value]="evidences()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.service.evidence.type') }}</th>
                  <th>{{ t('catalog.service.evidence.caption') }}</th>
                  <th>{{ t('catalog.service.evidence.preview') }}</th>
                  <th>{{ t('catalog.service.evidence.createdAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.evidenceType }}</td>
                  <td>{{ row.caption || '-' }}</td>
                  <td>
                    @if (isImageEvidence(row.contentReference)) {
                      <img class="thumb" [src]="row.contentReference" alt="evidence" />
                    } @else {
                      <span class="snippet">{{ row.contentReference | slice:0:48 }}{{ row.contentReference.length > 48 ? '…' : '' }}</span>
                    }
                  </td>
                  <td>{{ row.createdAtUtc }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tab-nav { display: flex; gap: 8px; margin-top: 12px; }
    .tab-nav button { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tab-nav button.active { border-color: #2563eb; color: #2563eb; font-weight: 600; }
    .form-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); padding-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .form-grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .manual-link { justify-self: start; border: none; background: none; padding: 0; color: #2563eb; cursor: pointer; font-size: 12px; text-decoration: underline; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .lines-panel { margin-top: 12px; }
    .lines-panel h4 { margin: 0 0 8px; font-size: 15px; }
    .consume-panel { margin-top: 12px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
    .evidence-form { margin-bottom: 10px; }
    .preview img, .thumb { max-width: 160px; max-height: 100px; border-radius: 8px; border: 1px solid #e2e8f0; }
    .signature-panel { display: grid; gap: 8px; }
    .signature-panel canvas { border: 1px solid #cbd5e1; border-radius: 8px; background: #fff; touch-action: none; cursor: crosshair; }
    .signature-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .snippet { font-size: 12px; color: #64748b; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class ServiceOrderDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly acting = signal(false);
  readonly error = signal('');
  readonly order = signal<ServiceOrder | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly serials = signal<InventorySerial[]>([]);
  readonly resources = signal<ServiceResource[]>([]);
  readonly activeTab = signal<ServiceTab>('general');

  isAutoCustomerReference = true;

  readonly priorityOptions = [
    { label: 'Low', value: 'Low' },
    { label: 'Normal', value: 'Normal' },
    { label: 'High', value: 'High' },
    { label: 'Critical', value: 'Critical' }
  ];

  readonly form = {
    orderNumber: '',
    title: '',
    description: '',
    offeringId: '',
    serialId: '',
    customerReferenceNumber: '',
    priority: 'Normal',
    slaPolicyCode: 'SLA-48H'
  };

  readonly scheduleForm = { startLocal: '', endLocal: '' };
  readonly assignForm = { resourceId: '', isPrimary: true };
  readonly consumeForm = { offeringId: '', serialId: '', quantity: 1, referenceNumber: '', unitCost: 0 };
  readonly completeForm = { completionNotes: '', checklistResultsJson: '{"install":"pass"}', requiresCustomerApproval: false, serviceRevenueAmount: 0 };
  readonly evidenceForm = { evidenceType: 'Photo', contentReference: '', caption: '' };
  readonly evidences = signal<ServiceCompletionEvidence[]>([]);

  @ViewChild('signatureCanvas') private signatureCanvas?: ElementRef<HTMLCanvasElement>;
  private signatureDrawing = false;

  readonly evidenceTypeOptions = computed(() => [
    { label: this.t('catalog.service.evidence.typePhoto'), value: 'Photo' },
    { label: this.t('catalog.service.evidence.typeSignature'), value: 'Signature' },
    { label: this.t('catalog.service.evidence.typeNote'), value: 'Note' }
  ]);

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly offeringOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.offerings().map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }))
  ]);
  readonly inventoryOfferingOptions = computed(() =>
    this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: `${o.code} - ${o.title}`, value: o.id }))
  );
  readonly serialOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.serials().map(s => ({ label: s.serialNumber, value: s.id }))
  ]);
  readonly consumeSerialOptions = computed(() => [
    { label: this.t('common.none'), value: '' },
    ...this.serials().map(s => ({ label: s.serialNumber, value: s.id }))
  ]);
  readonly resourceOptions = computed(() =>
    this.resources().map(r => ({ label: `${r.code} - ${r.name}`, value: r.id }))
  );

  ngOnInit(): void {
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.service.listInventorySerials().subscribe({ next: rows => this.serials.set(rows) });
    this.service.listServiceResources().subscribe({ next: rows => this.resources.set(rows) });
    this.load();
  }

  load(): void {
    if (this.isCreateMode()) {
      this.isAutoCustomerReference = true;
      this.loadCustomerReferencePreview();
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.service.getServiceOrder(id).subscribe({
      next: order => {
        this.applyOrder(order);
        this.loadEvidence(order.id);
        this.loading.set(false);
      },
      error: () => {
        this.order.set(null);
        this.error.set(this.t('catalog.service.orders.notFound'));
        this.loading.set(false);
      }
    });
  }

  canSchedule(): boolean {
    const status = this.order()?.status;
    return status === 'Draft' || status === 'Scheduled' || status === 'Assigned';
  }

  canAssign(): boolean {
    const status = this.order()?.status;
    return status === 'Draft' || status === 'Scheduled' || status === 'Assigned';
  }

  canStart(): boolean {
    const status = this.order()?.status;
    return status === 'Scheduled' || status === 'Assigned';
  }

  canConsumeParts(): boolean {
    const status = this.order()?.status;
    return status === 'Assigned' || status === 'InProgress';
  }

  canComplete(): boolean {
    return this.order()?.status === 'InProgress';
  }

  canCustomerApprove(): boolean {
    return this.order()?.status === 'PendingCustomerApproval';
  }

  resourceLabel(resourceId: string): string {
    const resource = this.resources().find(r => r.id === resourceId);
    return resource ? `${resource.code} - ${resource.name}` : resourceId;
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(o => o.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  save(): void {
    if (!this.isCreateMode()) return;
    if (!this.form.orderNumber.trim() || !this.form.title.trim()) {
      this.error.set(this.t('catalog.service.orders.requiredFields'));
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.service.createServiceOrder({
      orderNumber: this.form.orderNumber.trim(),
      title: this.form.title.trim(),
      description: this.form.description.trim(),
      offeringId: this.form.offeringId || null,
      serialId: this.form.serialId || null,
      customerReferenceNumber: this.isAutoCustomerReference ? '' : this.form.customerReferenceNumber.trim(),
      priority: this.form.priority.trim(),
      slaPolicyCode: this.form.slaPolicyCode.trim()
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/services/orders', created.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.service.orders.createFailed'));
      }
    });
  }

  schedule(): void {
    const order = this.order();
    if (!order || !this.scheduleForm.startLocal || !this.scheduleForm.endLocal) {
      this.error.set(this.t('catalog.service.scheduleRequired'));
      return;
    }

    this.acting.set(true);
    this.error.set('');
    this.service.scheduleServiceOrder(
      order.id,
      new Date(this.scheduleForm.startLocal).toISOString(),
      new Date(this.scheduleForm.endLocal).toISOString()
    ).subscribe({
      next: updated => {
        this.applyOrder(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.scheduleFailed'));
      }
    });
  }

  assign(): void {
    const order = this.order();
    if (!order || !this.assignForm.resourceId) {
      this.error.set(this.t('catalog.service.assignRequired'));
      return;
    }

    this.acting.set(true);
    this.error.set('');
    this.service.assignServiceOrder(order.id, this.assignForm.resourceId, this.assignForm.isPrimary).subscribe({
      next: () => {
        this.reloadOrder(order.id);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.assignFailed'));
      }
    });
  }

  start(): void {
    const order = this.order();
    if (!order) return;

    this.acting.set(true);
    this.error.set('');
    this.service.startServiceOrder(order.id).subscribe({
      next: updated => {
        this.applyOrder(updated);
        this.activeTab.set('execution');
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.startFailed'));
      }
    });
  }

  consumeParts(): void {
    const order = this.order();
    if (!order || !this.consumeForm.offeringId) {
      this.error.set(this.t('catalog.service.consumeRequired'));
      return;
    }

    const payload: ConsumeServicePartsPayload = {
      offeringId: this.consumeForm.offeringId,
      lotId: null,
      serialId: this.consumeForm.serialId || null,
      quantity: this.consumeForm.quantity,
      referenceNumber: this.consumeForm.referenceNumber.trim() || order.orderNumber,
      unitCost: this.consumeForm.unitCost
    };

    this.acting.set(true);
    this.error.set('');
    this.service.consumeServiceParts(order.id, payload).subscribe({
      next: () => {
        this.reloadOrder(order.id);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.consumeFailed'));
      }
    });
  }

  complete(): void {
    const order = this.order();
    if (!order) return;

    const payload: CompleteServiceOrderPayload = {
      completionNotes: this.completeForm.completionNotes.trim(),
      checklistResultsJson: this.completeForm.checklistResultsJson.trim() || '{}',
      requiresCustomerApproval: this.completeForm.requiresCustomerApproval,
      serviceRevenueAmount: this.completeForm.serviceRevenueAmount
    };

    this.acting.set(true);
    this.error.set('');
    this.service.completeServiceOrder(order.id, payload).subscribe({
      next: updated => {
        this.applyOrder(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.completeFailed'));
      }
    });
  }

  customerApprove(): void {
    const order = this.order();
    if (!order) return;

    this.acting.set(true);
    this.error.set('');
    this.service.customerApproveServiceOrder(order.id).subscribe({
      next: updated => {
        this.applyOrder(updated);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.approveFailed'));
      }
    });
  }

  private reloadOrder(id: string): void {
    this.service.getServiceOrder(id).subscribe({ next: order => this.applyOrder(order) });
  }

  addEvidence(): void {
    const order = this.order();
    if (this.evidenceForm.evidenceType === 'Signature' && !this.evidenceForm.contentReference.trim()) {
      this.applySignature();
    }
    if (!order || !this.evidenceForm.contentReference.trim()) {
      this.error.set(this.t('catalog.service.evidenceRequired'));
      return;
    }

    this.acting.set(true);
    this.service.addServiceCompletionEvidence(order.id, {
      evidenceType: this.evidenceForm.evidenceType,
      contentReference: this.evidenceForm.contentReference.trim(),
      caption: this.evidenceForm.caption.trim()
    }).subscribe({
      next: () => {
        this.evidenceForm.contentReference = '';
        this.evidenceForm.caption = '';
        this.clearSignature();
        this.loadEvidence(order.id);
        this.acting.set(false);
      },
      error: () => {
        this.acting.set(false);
        this.error.set(this.t('catalog.service.evidenceFailed'));
      }
    });
  }

  onEvidenceTypeChange(): void {
    this.evidenceForm.contentReference = '';
    this.clearSignature();
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.evidenceForm.contentReference = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
  }

  startSignature(event: MouseEvent): void {
    const ctx = this.getSignatureContext();
    if (!ctx) return;
    this.signatureDrawing = true;
    ctx.beginPath();
    ctx.moveTo(event.offsetX, event.offsetY);
  }

  drawSignature(event: MouseEvent): void {
    if (!this.signatureDrawing) return;
    const ctx = this.getSignatureContext();
    if (!ctx) return;
    ctx.lineTo(event.offsetX, event.offsetY);
    ctx.stroke();
  }

  startSignatureTouch(event: TouchEvent): void {
    event.preventDefault();
    const point = this.touchPoint(event);
    if (!point) return;
    const ctx = this.getSignatureContext();
    if (!ctx) return;
    this.signatureDrawing = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }

  drawSignatureTouch(event: TouchEvent): void {
    event.preventDefault();
    if (!this.signatureDrawing) return;
    const point = this.touchPoint(event);
    const ctx = this.getSignatureContext();
    if (!point || !ctx) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  }

  endSignature(): void {
    this.signatureDrawing = false;
  }

  applySignature(): void {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) return;
    this.evidenceForm.contentReference = canvas.toDataURL('image/png');
  }

  clearSignature(): void {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (this.evidenceForm.evidenceType === 'Signature') {
      this.evidenceForm.contentReference = '';
    }
  }

  isImageEvidence(contentReference: string): boolean {
    return contentReference.startsWith('data:image') || contentReference.startsWith('http');
  }

  private loadEvidence(orderId: string): void {
    this.service.listServiceCompletionEvidence(orderId).subscribe({
      next: rows => this.evidences.set(rows),
      error: () => this.evidences.set([])
    });
  }

  private getSignatureContext(): CanvasRenderingContext2D | null {
    const canvas = this.signatureCanvas?.nativeElement;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    return ctx;
  }

  private touchPoint(event: TouchEvent): { x: number; y: number } | null {
    const canvas = this.signatureCanvas?.nativeElement;
    const touch = event.touches[0] ?? event.changedTouches[0];
    if (!canvas || !touch) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }

  onCustomerReferenceChange(value: string): void {
    this.form.customerReferenceNumber = value;
    this.isAutoCustomerReference = false;
  }

  enableManualCustomerReference(): void {
    this.isAutoCustomerReference = false;
    this.form.customerReferenceNumber = '';
  }

  private loadCustomerReferencePreview(): void {
    if (!this.isCreateMode() || !this.isAutoCustomerReference) return;

    this.service.previewNextRunningNumber(
      CatalogRunningNumberKeys.customerReference,
      CatalogRunningNumberKeys.catalogOperationsParent,
      CatalogRunningNumberKeys.serviceOrderContext
    ).subscribe({
      next: preview => {
        if (preview && this.isAutoCustomerReference) {
          this.form.customerReferenceNumber = preview;
        }
      }
    });
  }

  private applyOrder(order: ServiceOrder): void {
    this.order.set(order);
    this.form.orderNumber = order.orderNumber;
    this.form.title = order.title;
    this.form.description = order.description;
    this.form.offeringId = order.offeringId ?? '';
    this.form.serialId = order.serialId ?? '';
    this.form.customerReferenceNumber = order.customerReferenceNumber;
    this.form.priority = order.priority;
    this.form.slaPolicyCode = order.slaPolicyCode;
    this.isAutoCustomerReference = false;
    if (order.scheduledStartUtc) {
      this.scheduleForm.startLocal = this.toLocalInput(order.scheduledStartUtc);
    }
    if (order.scheduledEndUtc) {
      this.scheduleForm.endLocal = this.toLocalInput(order.scheduledEndUtc);
    }
    if (order.completionNotes) {
      this.completeForm.completionNotes = order.completionNotes;
    }
    if (order.checklistResultsJson) {
      this.completeForm.checklistResultsJson = order.checklistResultsJson;
    }
  }

  private toLocalInput(iso: string): string {
    const date = new Date(iso);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
